/**
 * Temp-client handoff.
 *
 * A temp client arrives from the LeadFlow get-started page with a signed
 * handoff token in the URL (?t=<token>). We DO NOT decrypt anything in the
 * browser — the token is opaque here. Instead we send it to our backend
 * (POST /api/tenant/handoff), which verifies the LeadFlow signature and
 * returns the validated numeric client code. That code is then used for the
 * x-client-code header on API calls.
 *
 * If the token is missing, invalid, or expired, the app falls back to the
 * client-code screen, where the user types their code and the backend resolves
 * it through LeadFlow (same signed-token trust chain). The /shared route is
 * exempt.
 */

// Landing/get-started page — where users can obtain a fresh code.
// Config-driven so builds don't ship a hardcoded host.
export function getWebsiteUrl() {
  return window.APP_CONFIG?.websiteUrl || 'https://leadboat.in';
}

/** Read the handoff token from the URL (?t=<token>). */
export function getHandoffTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('t');
  return t || null;
}

export function isTempClientMode() {
  return typeof window !== 'undefined' && window.APP_CONFIG?.tempClientCode;
}

export function hasSessionTempClientCode() {
  return typeof window !== 'undefined' && !!sessionStorage.getItem('temp_client_code');
}

export function getTempClientCode() {
  return sessionStorage.getItem('temp_client_code');
}

/** Persist a resolved client code for this session. */
export function setTempClientCode(code) {
  sessionStorage.setItem('temp_client_code', code);
  if (window.APP_CONFIG) window.APP_CONFIG.clientCode = code;
}

/** Base URL for backend calls, always trailing-slashed (…/api/). */
function getApiBase() {
  const base = window.APP_CONFIG?.apiBaseUrl || '';
  if (!base) return null;
  return base.endsWith('/') ? base : base + '/';
}

/**
 * Exchange the handoff token for a validated client code via the backend.
 * Returns the numeric code on success, or null on failure.
 */
async function exchangeToken(token) {
  const base = getApiBase();
  if (!base) return null;
  try {
    const res = await fetch(`${base}tenant/handoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    const code = body?.data?.clientCode;
    return code && /^[0-9]{4,8}$/.test(String(code)) ? String(code) : null;
  } catch (err) {
    console.error('Handoff exchange failed:', err);
    return null;
  }
}

/**
 * Resolve a manually entered client code via our backend, which validates it
 * against LeadFlow and verifies the returned signature. Returns the validated
 * code on success; throws with a user-facing message on failure.
 */
export async function verifyClientCode(code) {
  const base = getApiBase();
  if (!base) throw new Error('Client verification is unavailable.');

  const res = await fetch(`${base}tenant/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_code: code }),
  });
  const body = await res.json().catch(() => null);

  if (!res.ok || !body?.success) {
    throw new Error(body?.message || 'Invalid client code. Please check and try again.');
  }
  const clientCode = body?.data?.clientCode;
  if (!clientCode || !/^[0-9]{4,8}$/.test(String(clientCode))) {
    throw new Error('Invalid client code. Please check and try again.');
  }
  return String(clientCode);
}

/**
 * Initialize the temp client code from the handoff token.
 * Call during app startup (before rendering the app).
 *
 * Returns the resolved code, or null when the app should ask the user for one
 * (the caller renders the client-code screen). The /shared route is exempt.
 */
export async function initTempClientCode() {
  // Already resolved earlier this session — reuse it.
  if (hasSessionTempClientCode()) {
    const stored = getTempClientCode();
    if (window.APP_CONFIG && stored) window.APP_CONFIG.clientCode = stored;
    return stored;
  }

  const token = getHandoffTokenFromUrl();
  if (!token) return null;

  const clientCode = await exchangeToken(token);
  if (!clientCode) return null;

  setTempClientCode(clientCode);

  // Strip the token from the URL so it isn't re-used or leaked via history.
  try {
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, clean || '/');
  } catch (e) { /* non-fatal */ }

  return clientCode;
}
